import React, { useEffect } from 'react';
import classNames from 'classnames';
import { Modal as AntdModal } from 'antd';
import Typography, { Variant } from '@lib/components/Typography/Typography';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Collapse from '@lib/components/Collapse/Collapse';
import {
  IconName,
  ModalIconColor,
  ModalMode,
} from '@lib/components/Modal/enums';
import LockBodyScroll from '@lib/components/LockBodyScroll/LockBodyScroll';
import useArrowKeyNavigation from '@lib/hooks/useArrowKeyNavigation';
import ModalIcon from './ModalIcon';
import styles from './Modal.module.scss';

interface Props {
  actions?: React.ReactElement;
  fixedActions?: React.ReactElement;
  caption?: string;
  children?: React.ReactElement;
  description?: React.ReactNode;
  descriptionAlign?: 'left' | 'center';
  descriptionChildren?: React.ReactNode;
  icon?: IconName;
  iconColor?: ModalIconColor;
  isOpen: boolean;
  modalBodyClassName?: string;
  modalContentClassName?: string;
  mode?: ModalMode;
  onClose?: () => void;
  title?: string;
  titleAlign?: 'left' | 'center';
  titleVariant?: Variant;
  renderAfterCaption?: React.ReactNode;
  // When true, modal content is unmounted on close.
  // Defaults to true to preserve existing behavior across the app.
  destroyOnHidden?: boolean;
}

function Modal(props: Props) {
  const {
    actions,
    fixedActions,
    caption,
    renderAfterCaption,
    children,
    description,
    descriptionAlign = 'center',
    descriptionChildren,
    icon,
    iconColor = ModalIconColor.Blue,
    isOpen,
    modalContentClassName,
    modalBodyClassName,
    onClose,
    title,
    titleAlign = 'center',
    titleVariant = 'h2',
    mode = ModalMode.Default,
    destroyOnHidden = true,
  } = props;

  useEffect(() => {
    const { body } = document;
    let scrollY = 0;

    if (isOpen) {
      scrollY = window.scrollY;
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';
    }

    return () => {
      if (body.style.position === 'fixed') {
        body.style.position = '';
        body.style.top = '';
        body.style.width = '';
        window.scrollTo(0, scrollY);
      }
    };
  }, [isOpen]);

  let isCloseBtnVisible = false;
  if (onClose) isCloseBtnVisible = true;

  const afterOpenModal = () => undefined;

  const onRequestClose = () => {
    if (onClose) onClose();
  };

  const classNamesObj = {
    mask: styles.mask,
  };

  // Use default portal container (document.body) so @rc-component/portal
  // can apply its built-in scroll locker correctly.
  const getContainer = () => document.body;

  const hasModalTopRender = !!(
    icon ||
    title ||
    description ||
    descriptionChildren
  );

  useArrowKeyNavigation(
    onClose
      ? [
          {
            keys: 'Escape',
            handler: onRequestClose,
          },
        ]
      : [],
  );

  return (
    <AntdModal
      afterOpenChange={afterOpenModal}
      centered
      classNames={classNamesObj}
      closeIcon={null}
      destroyOnHidden={destroyOnHidden}
      footer={null}
      getContainer={getContainer}
      maskClosable={false}
      onCancel={onRequestClose}
      open={isOpen}
      rootClassName={classNames(`mode-${mode}`)}
      width={mode === ModalMode.Default ? 680 : '100%'}
    >
      <LockBodyScroll />
      <div className={styles.modalWrap}>
        <div className={styles.modalHeader}>
          {caption && (
            <div className={styles.modalHeaderRow}>
              <Typography
                variant="body"
                strong
                className={styles.modalHeaderTitle}
              >
                {caption}
              </Typography>{renderAfterCaption}
            </div>
          )}
          {isCloseBtnVisible && (
            <IconButton
              icon="close"
              type={IconButtonTypes.PrimaryOutlined}
              onClick={onRequestClose}
              className={styles.modalHeaderLeftIcon}
            />
          )}
        </div>
        <div className={styles.modalScrollWrap}>
          <div className={classNames(styles.modalBody, modalBodyClassName)}>
            {hasModalTopRender ? (
              <div>
                {icon && (
                  <div className={styles.modalIcon}>
                    <ModalIcon icon={icon} iconColor={iconColor} />
                  </div>
                )}
                {title && (
                  <Collapse minHeight={120} useCropBlurWhite>
                    <Typography
                      variant={titleVariant}
                      strong
                      className={classNames(styles.modalTitle, {
                        [styles.modalTitleLeft]: titleAlign === 'left',
                        [styles.modalTitleCenter]: titleAlign === 'center',
                      })}
                    >
                      {title}
                    </Typography>
                  </Collapse>
                )}
                {description && (
                  <Typography
                    variant="body"
                    className={classNames(styles.modalDescription, {
                      [styles.modalDescriptionCentered]:
                        descriptionAlign === 'center',
                    })}
                  >
                    {description}
                  </Typography>
                )}
                {descriptionChildren && <div>{descriptionChildren}</div>}
              </div>
            ) : null}
            <div
              className={classNames(styles.modalContent, modalContentClassName)}
            >
              {children}
            </div>
            {actions !== undefined && actions}
          </div>
        </div>
        {fixedActions !== undefined && (
          <div className={styles.modalFixedActions}>{fixedActions}</div>
        )}
      </div>
    </AntdModal>
  );
}

export default Modal;
